import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { KeyRound, LogOut } from "lucide-react";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);

  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated");
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-white" style={{ backgroundColor: "#F26A24" }}>
            <KeyRound size={26} />
          </div>
          <div className="font-heading text-2xl font-bold tracking-tight mt-4">GCOMS.RN</div>
        </div>
        <div className="module-card p-10 bg-white">
          <div className="uppercase-label">{forced ? "First-time setup" : "Account settings"}</div>
          <h1 className="font-heading text-3xl font-semibold mt-3">
            {forced ? "Set your password" : "Change password"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {forced
              ? "Replace the temporary password we emailed you with one you'll remember."
              : "Choose a new password for your account."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium">
                {forced ? "Temporary password" : "Current password"}
              </label>
              <input
                data-testid="cp-current-input"
                type="password"
                required
                value={form.current}
                onChange={(e) => setForm({ ...form, current: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium">New password (min 8 chars)</label>
              <input
                data-testid="cp-new-input"
                type="password"
                required
                minLength={8}
                value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Confirm new password</label>
              <input
                data-testid="cp-confirm-input"
                type="password"
                required
                minLength={8}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
              />
            </div>
            <button
              data-testid="cp-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full mt-2 py-3 rounded-md bg-ink text-bone font-medium hover:bg-ink/90 disabled:opacity-60"
            >
              {submitting ? "Updating..." : "Update password"}
            </button>
          </form>
          {forced && (
            <button
              onClick={logout}
              data-testid="cp-logout-link"
              className="mt-5 text-xs text-muted-foreground hover:text-ink inline-flex items-center gap-1.5"
            >
              <LogOut size={12} /> Sign out instead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
